import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectGroup,
  SelectItem,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Checkbox } from "../../../components/ui/checkbox";
import apiClient from "../../../utils/apiClient";
import { Input } from "../../../components/ui/input";
import { Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import server from "../../../utils/serverConfig";

const statusOptions = [
  { value: "active", label: "active" },
  { value: "inactive", label: "inactive" },
  { value: "deactivated", label: "deactivated" },
  { value: "dummy", label: "dummy" },
];

const labelOptions = [
  { value: "clearmind", label: "clearmind" },
  { value: "satyam", label: "satyam" },
];

export function FyersUserForm({ 
  handleAddFyersUserIndexMuliplier, 
  onClose, 
  proxyData, 
  isOnholdLegality, 
  isLegalitiesComplete, 
  isPolarisOrPolarisLite,
  isWizard = false,
  onWizardSubmit,
  prefilledName = "",
  prefilledPhone = "",
  prefilledClientId = "",
  prefilledTotpKey = "",
  prefilledPin = "",
  prefilledAppId = "",
  prefilledAppType = "",
  prefilledAppSecret = "",
  prefilledRedirectUrl = "",
  prefilledAppIdType = "",
  prefilledLabel = "",
  prefilledProxyUser = "",
  prefilledProxyPassword = "",
  prefilledProxyHost = "",
  prefilledProxyPort = 0,
  prefilledProxyStaticIp = "",
  prefilledProxyToggle = false
}) {
  const [clientId, setClientId] = useState(prefilledClientId || "");
  const [name, setName] = useState(prefilledName || "");
  const [totpKey, setTotpKey] = useState(prefilledTotpKey || "");
  const [pin, setPin] = useState(prefilledPin || "");
  const [appId, setAppId] = useState(prefilledAppId || "");
  const [appType, setAppType] = useState(prefilledAppType || "");
  const [appSecret, setAppSecret] = useState(prefilledAppSecret || "");
  const [redirectUrl, setRedirectUrl] = useState(prefilledRedirectUrl || "");
  const [appIdType, setAppIdType] = useState(prefilledAppIdType || "");
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOnholdLegality && !isLegalitiesComplete) {
      setStatus("inactive");
    }
  }, [isOnholdLegality, isLegalitiesComplete]);

  useEffect(() => {
    if (isPolarisOrPolarisLite) {
      setMultiplier(0);
      setBankniftyMultiplier(0);
      setFinniftyMultiplier(0);
      setMidcpniftyMultiplier(0);
      setSensexMultiplier(0);
    }
  }, [isPolarisOrPolarisLite]);
  const [phoneNumber, setPhoneNumber] = useState(prefilledPhone || "");
  const [label, setLabel] = useState(prefilledLabel || "");
  const [activationUrl, setActivationUrl] = useState("");
  const [multiplier, setMultiplier] = useState(1);
  const [bankniftyMultiplier, setBankniftyMultiplier] = useState(0);
  const [finniftyMultiplier, setFinniftyMultiplier] = useState(0);
  const [midcpniftyMultiplier, setMidcpniftyMultiplier] = useState(0);
  const [sensexMultiplier, setSensexMultiplier] = useState(0);
  const [proxyUser, setProxyUser] = useState(prefilledProxyUser || "");
  const [proxyPassword, setProxyPassword] = useState(prefilledProxyPassword || "");
  const [proxyHost, setProxyHost] = useState(prefilledProxyHost || "");
  const [proxyPort, setProxyPort] = useState(prefilledProxyPort || 0);
  const [proxyToggle, setProxyToggle] = useState(prefilledProxyToggle || false);
  const [proxyStaticIp, setProxyStaticIp] = useState(prefilledProxyStaticIp || "");

  // ── Sync prefilled props asynchronously ───────────────────────────────────
  useEffect(() => {
    if (prefilledClientId) setClientId(prefilledClientId);
    if (prefilledName) setName(prefilledName);
    if (prefilledPhone) setPhoneNumber(prefilledPhone);
    if (prefilledTotpKey) setTotpKey(prefilledTotpKey);
    if (prefilledPin) setPin(prefilledPin);
    if (prefilledAppId) setAppId(prefilledAppId);
    if (prefilledAppType) setAppType(prefilledAppType);
    if (prefilledAppSecret) setAppSecret(prefilledAppSecret);
    if (prefilledRedirectUrl) setRedirectUrl(prefilledRedirectUrl);
    if (prefilledAppIdType) setAppIdType(prefilledAppIdType);
    if (prefilledLabel) setLabel(prefilledLabel);
    if (prefilledProxyUser) setProxyUser(prefilledProxyUser);
    if (prefilledProxyPassword) setProxyPassword(prefilledProxyPassword);
    if (prefilledProxyHost) setProxyHost(prefilledProxyHost);
    if (prefilledProxyPort !== undefined && prefilledProxyPort !== null) setProxyPort(prefilledProxyPort);
    if (prefilledProxyStaticIp) setProxyStaticIp(prefilledProxyStaticIp);
    if (prefilledProxyToggle !== undefined && prefilledProxyToggle !== null) setProxyToggle(prefilledProxyToggle);
  }, [
    prefilledClientId, prefilledName, prefilledPhone, prefilledTotpKey,
    prefilledPin, prefilledAppId, prefilledAppType, prefilledAppSecret,
    prefilledRedirectUrl, prefilledAppIdType, prefilledLabel, prefilledProxyUser,
    prefilledProxyPassword, prefilledProxyHost, prefilledProxyPort,
    prefilledProxyStaticIp, prefilledProxyToggle
  ]);

  useEffect(() => {
    if (proxyData) {
      setProxyUser(proxyData.proxy_user || "");
      setProxyPassword(proxyData.proxy_password || "");
      setProxyHost(proxyData.proxy_host || "");
      setProxyPort(proxyData.proxy_port || 0);
      setProxyStaticIp(proxyData.proxy_static_ip || "");
      setProxyToggle(true); // Automatically turn on toggle since a proxy was acquired
    }
  }, [proxyData]);

  // States for password visibility
  const [showTotpKey, setShowTotpKey] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showAppSecret, setShowAppSecret] = useState(false);
  const [showProxyPassword, setShowProxyPassword] = useState(false);

  const handleAddFyersUser = () => {
    const cId = (clientId || "").trim();
    const nm = (name || "").trim();
    const totp = (totpKey || "").trim();
    const p = (pin || "").trim();
    const aId = (appId || "").trim();
    const aType = (appType || "").trim();
    const aSec = (appSecret || "").trim();
    const rUrl = (redirectUrl || "").trim();
    const aIdType = (appIdType || "").trim();
    const st = (status || "").trim();
    const ph = (phoneNumber || "").trim();
    const lbl = (label || "").trim();
    const pUser = (proxyUser || "").trim();
    const pPwd = (proxyPassword || "").trim();
    const pHost = (proxyHost || "").trim();
    const pStaticIp = (proxyStaticIp || "").trim();

    const data = {
      client_id: cId,
      name: nm,
      totp_key: totp,
      pin: p,
      app_id: aId,
      app_type: aType,
      app_secret: aSec,
      redirect_url: rUrl,
      app_id_type: aIdType,
      status: st,
      phone_number: ph,
      label: lbl,
      proxy_user: pUser,
      proxy_password: pPwd,
      proxy_host: pHost,
      proxy_port: parseInt(proxyPort) || 0,
      proxy_toggle: !!proxyToggle,
      proxy_static_ip: pStaticIp || null,
    };

    if (
      !data.client_id ||
      !data.name ||
      !data.totp_key ||
      !data.pin ||
      !data.app_id ||
      !data.app_type ||
      !data.app_secret ||
      !data.redirect_url ||
      !data.app_id_type ||
      !data.status ||
      !data.phone_number ||
      !data.label ||
      (multiplier === "" || multiplier === null ) ||
      (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
      (finniftyMultiplier === "" || finniftyMultiplier === null) ||
      (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
      (sensexMultiplier === "" || sensexMultiplier === null)
    ) {
      console.log("Missing required fields:", data);
      toast.error("Missing required fields !!", {
            duration: 5000,
      });
      return;
    }

    // ── Wizard mode: submit for approval (no broker API call) ─────────────────
    if (isWizard && onWizardSubmit) {
      onWizardSubmit({
        client_id: cId,
        brokerData: {
          ...data,
          multiplier: parseInt(multiplier) || 0,
          banknifty_multiplier: parseInt(bankniftyMultiplier) || 0,
          finnifty_multiplier: parseInt(finniftyMultiplier) || 0,
          midcpnifty_multiplier: parseInt(midcpniftyMultiplier) || 0,
          sensex_multiplier: parseInt(sensexMultiplier) || 0,
        },
      });
      return;
    }

    console.log("New Fyers User Data", data);

    let config = {
      method: "post",
      url: `${server.godseye.baseUrl}add-fyers-user`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    setIsSubmitting(true);
    apiClient
      .request(config)
      .then((response) => {
        console.log("Server response:", response.data);
        if (response.data.status === 200) {
          toast.success("New Fyers user added successfully!", {
            duration: 5000,
          });
          console.log("New Fyers user added successfully!");
          handleAddFyersUserIndexMuliplier(clientId, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier);
          onClose(true); // Close with success
        } else {
          onClose(false);
        }
      })
      .catch((error) => {
        console.error("Failed to add new Fyers user", error);
        onClose(false);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleActivateUser = () => {
    const data = {
      app_id: appId,
      app_type: appType,
      app_secret: appSecret,
      redirect_url: redirectUrl,
    };

    if (
      !data.app_id ||
      !data.app_type ||
      !data.app_secret ||
      !data.redirect_url
    ) {
      console.log("Missing required fields for activation:", data);
      toast.error("Please fill in all required fields for activation.");
      return;
    }

    console.log("User Data for Account Activation ", data);

    let mainConfig = {
      method: "POST",
      url: `${server.godseye.baseUrl}activate-user`,
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(data),
    };

    apiClient
      .request(mainConfig)
      .then((response) => {
        console.log("Main API activation response:", response.data);
        if (response.data && response.data.url) {
          setActivationUrl(response.data.url);
        }
      })
      .catch((error) => {
        console.error("Failed to activate user with main API", error);
        setActivationUrl(`Error: ${error.message}`);
      });
  };

  return (
    <div className="font-inter space-y-4">
      <div className="grid grid-cols-4 gap-6 py-4">
        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Client Id <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Name <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">TOTP Key <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Input
              className="text-white pr-10"
              type={showTotpKey ? "text" : "password"}
              value={totpKey}
              onChange={(e) => setTotpKey(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowTotpKey(!showTotpKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
            >
              {showTotpKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Pin <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Input
              className="text-white pr-10"
              type={showPin ? "text" : "password"}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPin(!showPin)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
            >
              {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">App Id <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">App Type <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            value={appType}
            onChange={(e) => setAppType(e.target.value)}
          />
        </div>
        
        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">App Secret <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Input
              className="text-white pr-10"
              type={showAppSecret ? "text" : "password"}
              value={appSecret}
              onChange={(e) => setAppSecret(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowAppSecret(!showAppSecret)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
            >
              {showAppSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Redirect URL <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            value={redirectUrl}
            onChange={(e) => setRedirectUrl(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">App Id Type <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            value={appIdType}
            onChange={(e) => setAppIdType(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Status <span className="text-red-500">*</span></Label>
          <Select value={status} onValueChange={(val) => setStatus(val)} disabled={isOnholdLegality && !isLegalitiesComplete}>
            <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
              <SelectValue placeholder="Select Status" />
            </SelectTrigger>
            <SelectContent className="border-zinc-700">
              <SelectGroup>
                {statusOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Phone Number <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Label <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="text"
            list="label-options"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Select or type label"
          />
          <datalist id="label-options">
            {labelOptions.map((l) => (
              <option key={l.value} value={l.value} />
            ))}
          </datalist>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Multiplier Number <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="number"
            value={multiplier}
            onChange={(e) => setMultiplier(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Bank Nifty Multiplier <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="number"
            value={bankniftyMultiplier}
            onChange={(e) => setBankniftyMultiplier(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Fin Nifty Multiplier <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="number"
            value={finniftyMultiplier}
            onChange={(e) => setFinniftyMultiplier(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Midcap Nifty Multiplier <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="number"
            value={midcpniftyMultiplier}
            onChange={(e) => setMidcpniftyMultiplier(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Sensex Multiplier <span className="text-red-500">*</span></Label>
          <Input
            className="text-white"
            type="number"
            value={sensexMultiplier}
            onChange={(e) => setSensexMultiplier(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy User</Label>
          <Input
            className="text-white"
            type="text"
            value={proxyUser}
            onChange={(e) => setProxyUser(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy Password</Label>
          <div className="relative">
            <Input
              className="text-white pr-10"
              type={showProxyPassword ? "text" : "password"}
              value={proxyPassword}
              onChange={(e) => setProxyPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowProxyPassword(!showProxyPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-200"
            >
              {showProxyPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy Host</Label>
          <Input
            className="text-white"
            type="text"
            value={proxyHost}
            onChange={(e) => setProxyHost(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy Port</Label>
          <Input
            className="text-white"
            type="number"
            value={proxyPort}
            onChange={(e) => setProxyPort(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 items-center gap-4">
          <Label className="text-white">Proxy Static IP</Label>
          <Input
            className="text-white"
            type="text"
            placeholder="e.g. 2402:1f00:8300:989::1"
            value={proxyStaticIp}
            onChange={(e) => setProxyStaticIp(e.target.value)}
          />
        </div>

        <div className="flex items-center space-x-2 h-full pt-4">
          <Checkbox
            id="proxy-toggle"
            checked={proxyToggle}
            onCheckedChange={(checked) => setProxyToggle(checked)}
          />
          <Label htmlFor="proxy-toggle" className="text-white cursor-pointer">
            Proxy Toggle
          </Label>
        </div>
      </div>

      {activationUrl && (
        <div className="mt-4 p-3 bg-zinc-900 rounded border border-zinc-700">
          <Label className="text-white block mb-2">Activation URL:</Label>
          <div className="bg-zinc-800 p-2 rounded text-green-400 overflow-auto break-all">
            {activationUrl}
          </div>
        </div>
      )}

      <div className="mt-6 flex space-x-4 justify-end">
        <Button
          disabled={
            (clientId === "" || clientId === null) ||
            (name === "" || name === null) ||
            (totpKey === "" || totpKey === null) ||
            (pin === "" || pin === null) ||
            (appId === "" || appId === null) ||
            (appType === "" || appType === null) ||
            (appIdType === "" || appIdType === null) ||
            (appSecret === "" || appSecret === null) ||
            (redirectUrl === "" || redirectUrl === null) ||
            (status === "" || status === null) ||
            (phoneNumber === "" || phoneNumber === null) ||
            (label === "" || label === null) ||
            (multiplier === "" || multiplier === null) ||
            (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
            (finniftyMultiplier === "" || finniftyMultiplier === null) ||
            (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
            (sensexMultiplier === "" || sensexMultiplier === null)
          }
          type="button"
          className="font-inter text-[16px]  bg-[#07F2C7]  text-black font-medium hover:bg-[#07F2C7]/80 "
          onClick={handleActivateUser}
        >
          Activate User
        </Button>
        <Button
          disabled={
            isSubmitting ||
            (clientId === "" || clientId === null) ||
            (name === "" || name === null) ||
            (totpKey === "" || totpKey === null) ||
            (pin === "" || pin === null) ||
            (appId === "" || appId === null) ||
            (appType === "" || appType === null) ||
            (appIdType === "" || appIdType === null) ||
            (appSecret === "" || appSecret === null) ||
            (redirectUrl === "" || redirectUrl === null) ||
            (status === "" || status === null) ||
            (phoneNumber === "" || phoneNumber === null) ||
            (label === "" || label === null) ||
            (multiplier === "" || multiplier === null) ||
            (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
            (finniftyMultiplier === "" || finniftyMultiplier === null) ||
            (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
            (sensexMultiplier === "" || sensexMultiplier === null)
          }
          type="submit"
          className="font-inter text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700 disabled:opacity-50"
          onClick={handleAddFyersUser}
        >
          {isSubmitting ? "Adding..." : isWizard ? "Submit for Approval" : "Add Fyers User"}
        </Button>
      </div>
    </div>
  );
}


















































// import { useState } from "react";
// import { Button } from "../../../components/ui/button";
// import {
//   Select,
//   SelectTrigger,
//   SelectValue,
//   SelectContent,
//   SelectGroup,
//   SelectItem,
// } from "../../../components/ui/select";
// import { Label } from "../../../components/ui/label";
// import apiClient from "../../../utils/apiClient";
// import { Input } from "../../../components/ui/input";
// import toast from "react-hot-toast";

// const statusOptions = [
//   { value: "active", label: "active" },
//   { value: "inactive", label: "inactive" },
//   { value: "deactivated", label: "deactivated" },
//   { value: "dummy", label: "dummy" },
// ];

// export function FyersUserForm({ handleAddFyersUserIndexMuliplier, onClose }) {
//   const [clientId, setClientId] = useState("");
//   const [name, setName] = useState("");
//   const [totpKey, setTotpKey] = useState("");
//   const [pin, setPin] = useState("");
//   const [appId, setAppId] = useState("");
//   const [appType, setAppType] = useState("");
//   const [appSecret, setAppSecret] = useState("");
//   const [redirectUrl, setRedirectUrl] = useState("");
//   const [appIdType, setAppIdType] = useState("");
//   const [status, setStatus] = useState("");
//   const [phoneNumber, setPhoneNumber] = useState("");
//   const [activationUrl, setActivationUrl] = useState("");
//   const [multiplier, setMultiplier] = useState(1);
//   const [bankniftyMultiplier, setBankniftyMultiplier] = useState(0);
//   const [finniftyMultiplier, setFinniftyMultiplier] = useState(0);
//   const [midcpniftyMultiplier, setMidcpniftyMultiplier] = useState(0);
//   const [sensexMultiplier, setSensexMultiplier] = useState(0);

//   const handleAddFyersUser = () => {
//     const data = {
//       client_id: clientId,
//       name: name,
//       totp_key: totpKey,
//       pin: pin,
//       app_id: appId,
//       app_type: appType,
//       app_secret: appSecret,
//       redirect_url: redirectUrl,
//       app_id_type: appIdType,
//       status: status,
//       phone_number: phoneNumber,
//     };

//     if (
//       !data.client_id ||
//       !data.name ||
//       !data.totp_key ||
//       !data.pin ||
//       !data.app_id ||
//       !data.app_type ||
//       !data.app_secret ||
//       !data.redirect_url ||
//       !data.app_id_type ||
//       !data.status ||
//       !data.phone_number ||
//       (multiplier === "" || multiplier === null ) ||
//       (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
//       (finniftyMultiplier === "" || finniftyMultiplier === null) ||
//       (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
//       (sensexMultiplier === "" || sensexMultiplier === null)
//     ) {
//       console.log("Missing required fields:", data);
//       toast.error("Missing required fields !!", {
//             duration: 5000,
//       });
//       return;
//     }
//     console.log("New Fyers User Data", data);
//     console.log("New Fyers User multipliers", { 
//       multiplier, 
//       bankniftyMultiplier, 
//       finniftyMultiplier, 
//       midcpniftyMultiplier, 
//       sensexMultiplier 
//     });

//     let config = {
//       method: "post",
//       url: "https://godseye.server.sypto.xyz/add-fyers-user",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       data: JSON.stringify(data),
//     };

//     apiClient
//       .request(config)
//       .then((response) => {
//         console.log("Server response:", response.data);
//         if (response.data.status === 200) {
//           toast.success("New Fyers user added successfully!", {
//             duration: 5000,
//           });
//           console.log("New Fyers user added successfully!");
//           handleAddFyersUserIndexMuliplier(clientId, name, multiplier, bankniftyMultiplier, finniftyMultiplier, midcpniftyMultiplier, sensexMultiplier);
//         }
//       })
//       .catch((error) => {
//         console.error("Failed to add new Fyers user", error);
//       })
//       .finally(() => {
//         onClose();
//       });
//   };

//   const handleActivateUser = () => {
//     const data = {
//       app_id: appId,
//       app_type: appType,
//       app_secret: appSecret,
//       redirect_url: redirectUrl,
//     };

//     if (
//       !data.app_id ||
//       !data.app_type ||
//       !data.app_secret ||
//       !data.redirect_url
//     ) {
//       console.log("Missing required fields for activation:", data);
//       toast.error("Please fill in all required fields for activation.");
//       return;
//     }

//     console.log("User Data for Account Activation ", data);

//     let mainConfig = {
//       method: "POST",
//       url: "https://godseye.server.sypto.xyz/activate-user",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       data: JSON.stringify(data),
//     };

//     apiClient
//       .request(mainConfig)
//       .then((response) => {
//         console.log("Main API activation response:", response.data);
//         if (response.data && response.data.url) {
//           setActivationUrl(response.data.url);
//         }
//       })
//       .catch((error) => {
//         console.error("Failed to activate user with main API", error);
//         setActivationUrl(`Error: ${error.message}`);
//       });
//   };

//   return (
//     <div className="font-inter space-y-4">
//       <div className="grid grid-cols-4 gap-6 py-4">
//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Client Id <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={clientId}
//             onChange={(e) => setClientId(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Name <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={name}
//             onChange={(e) => setName(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">TOTP Key <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={totpKey}
//             onChange={(e) => setTotpKey(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Pin <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={pin}
//             onChange={(e) => setPin(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">App Id <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={appId}
//             onChange={(e) => setAppId(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">App Type <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={appType}
//             onChange={(e) => setAppType(e.target.value)}
//           />
//         </div>
        
//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">App Secret <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={appSecret}
//             onChange={(e) => setAppSecret(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Redirect URL <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={redirectUrl}
//             onChange={(e) => setRedirectUrl(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">App Id Type <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="text"
//             value={appIdType}
//             onChange={(e) => setAppIdType(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Status <span className="text-red-500">*</span></Label>
//           <Select value={status} onValueChange={(val) => setStatus(val)}>
//             <SelectTrigger className="text-white text-[15px] hover:bg-zinc-800">
//               <SelectValue placeholder="Select Status" />
//             </SelectTrigger>
//             <SelectContent className="border-zinc-700">
//               <SelectGroup>
//                 {statusOptions.map((s) => (
//                   <SelectItem key={s.value} value={s.value}>
//                     {s.label}
//                   </SelectItem>
//                 ))}
//               </SelectGroup>
//             </SelectContent>
//           </Select>
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Phone Number <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="number"
//             value={phoneNumber}
//             onChange={(e) => setPhoneNumber(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Multiplier Number <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="number"
//             value={multiplier}
//             onChange={(e) => setMultiplier(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Bank Nifty Multiplier <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="number"
//             value={bankniftyMultiplier}
//             onChange={(e) => setBankniftyMultiplier(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Fin Nifty Multiplier <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="number"
//             value={finniftyMultiplier}
//             onChange={(e) => setFinniftyMultiplier(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Midcap Nifty Multiplier <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="number"
//             value={midcpniftyMultiplier}
//             onChange={(e) => setMidcpniftyMultiplier(e.target.value)}
//           />
//         </div>

//         <div className="grid grid-cols-1 items-center gap-4">
//           <Label className="text-white">Sensex Multiplier <span className="text-red-500">*</span></Label>
//           <Input
//             className="text-white"
//             type="number"
//             value={sensexMultiplier}
//             onChange={(e) => setSensexMultiplier(e.target.value)}
//           />
//         </div>
//       </div>

//       {activationUrl && (
//         <div className="mt-4 p-3 bg-zinc-900 rounded border border-zinc-700">
//           <Label className="text-white block mb-2">Activation URL:</Label>
//           <div className="bg-zinc-800 p-2 rounded text-green-400 overflow-auto break-all">
//             {activationUrl}
//           </div>
//         </div>
//       )}

//       <div className="mt-6 flex space-x-4 justify-end">
//         <Button
//           disabled={
//             (clientId === "" || clientId === null) ||
//             (name === "" || name === null) ||
//             (totpKey === "" || totpKey === null) ||
//             (pin === "" || pin === null) ||
//             (appId === "" || appId === null) ||
//             (appType === "" || appType === null) ||
//             (appIdType === "" || appIdType === null) ||
//             (appSecret === "" || appSecret === null) ||
//             (redirectUrl === "" || redirectUrl === null) ||
//             (status === "" || status === null) ||
//             (phoneNumber === "" || phoneNumber === null) ||
//             (multiplier === "" || multiplier === null) ||
//             (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
//             (finniftyMultiplier === "" || finniftyMultiplier === null) ||
//             (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
//             (sensexMultiplier === "" || sensexMultiplier === null)
//           }
//           type="button"
//           className="font-inter text-[16px]  bg-[#07F2C7]  text-black font-medium hover:bg-[#07F2C7]/80 "
//           onClick={handleActivateUser}
//         >
//           Activate User
//         </Button>
//         <Button
//           disabled={
//             (clientId === "" || clientId === null) ||
//             (name === "" || name === null) ||
//             (totpKey === "" || totpKey === null) ||
//             (pin === "" || pin === null) ||
//             (appId === "" || appId === null) ||
//             (appType === "" || appType === null) ||
//             (appIdType === "" || appIdType === null) ||
//             (appSecret === "" || appSecret === null) ||
//             (redirectUrl === "" || redirectUrl === null) ||
//             (status === "" || status === null) ||
//             (phoneNumber === "" || phoneNumber === null) ||
//             (multiplier === "" || multiplier === null) ||
//             (bankniftyMultiplier === "" || bankniftyMultiplier === null) ||
//             (finniftyMultiplier === "" || finniftyMultiplier === null) ||
//             (midcpniftyMultiplier === "" || midcpniftyMultiplier === null) ||
//             (sensexMultiplier === "" || sensexMultiplier === null)
//           }
//           type="submit"
//           className="font-inter text-[16px] font-normal bg-zinc-800 text-white hover:bg-zinc-700"
//           onClick={handleAddFyersUser}
//         >
//           Add Fyers User
//         </Button>
//       </div>
//     </div>
//   );
// }